import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { getScheduleByIdApi, getInvitationsApi } from "../../../API/schedule";
import { setMeetingSuccess as setMeetingAction } from "../../../store/actions/schedule";
import { errorResponseHandler } from "../../../utils/reposeAlert";
import { formatDate } from "../../../utils/formateDate";
import { byStartDateAsc } from "../../../utils/meetingStatus";

import MeetingBox from "./MeetingBox";
import MeetingModal from "./MeetingModal";
import RescheduleModal from "./RescheduleModal";
import AskForRescheduleModal from "./AskForRescheduleModal";
import theme from "../../../theme/theme";

const Meetings = ({ getMeetings }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const meetings = useSelector((state) => state.schedules.meetings);
  const selectedMeeting = useSelector((state) => state.schedules.meeting);

  const [openMeetingModal, setOpenMeetingModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [showAskForRescheduleModal, setShowAskForRescheduleModal] =
    useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  // Pending invitations = "Meeting Requests" (meetings you were invited to but
  // haven't accepted yet). These don't appear in the meeting list.
  const [requests, setRequests] = useState([]);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await getInvitationsApi();
      if (response.status === 200) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? [];
        // Only still-pending invitations are actionable requests, ordered by
        // the meeting's date & time (soonest first).
        setRequests(
          list
            .filter((inv) => inv.status === "pending")
            .sort((a, b) => byStartDateAsc(a.schedule, b.schedule))
        );
      }
    } catch (error) {
      // non-fatal — the meeting list still works
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Refresh both the meeting list and the requests after any action.
  const refreshAll = useCallback(() => {
    if (getMeetings) getMeetings();
    fetchRequests();
  }, [getMeetings, fetchRequests]);

  const openMeetingModalHandler = (meetingId) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    dispatch(setMeetingAction(meeting));
    setOpenMeetingModal(true);
  };

  const closeMeetingModalHandler = () => {
    setOpenMeetingModal(false);
    // Clear the ?meeting= param via the router (keeps react-router's location in
    // sync so re-opening the same meeting from a notification works again).
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  };

  const toggleRescheduleModal = (open) => {
    setOpenMeetingModal(false);
    setOpenRescheduleModal(open);
  };

  const askForRescheduleHandler = (open, meetingId) => {
    setOpenMeetingModal(false);
    setShowAskForRescheduleModal(true);
    setSelectedMeetingId(meetingId);
  };

  // Open the meeting details modal whenever the ?meeting= query param is present.
  // Depend on location.search (react-router) so it also fires on SOFT navigation
  // — e.g. tapping a meeting notification — not only on a full page refresh.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const meetingId = searchParams.get("meeting");
    if (meetingId) {
      openSelectedMeetingModal(meetingId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const openSelectedMeetingModal = async (meetingId) => {
    try {
      // Fetch just the one meeting by id (full details incl. invitations) so
      // the modal shows the correct actions.
      const response = await getScheduleByIdApi(meetingId);
      if (response.status === 200 && response.data) {
        dispatch(setMeetingAction(response.data));
        setOpenMeetingModal(true);
      }
    } catch (error) {
      errorResponseHandler("Failed", error.response?.data || error.message);
    }
  };

  const sectionTitleSx = {
    color: theme.palette.secondary.main,
    fontWeight: 700,
    fontSize: "1.1rem",
    mb: 1,
  };

  const headRowSx = {
    backgroundColor: "#f5f8ff",
    "& th": {
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.primary.main,
      textTransform: "uppercase",
      borderBottom: "2px solid #dbe3f3",
      py: 2,
    },
    display: { xs: "none", md: "table-row" },
  };

  return (
    <>
      {/* Meeting Requests — pending invitations awaiting your response */}
      {requests.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography sx={sectionTitleSx}>Meeting Requests</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={headRowSx}>
                  <TableCell align="center">Meeting ID</TableCell>
                  <TableCell align="center">Title</TableCell>
                  <TableCell align="center">Date & Time</TableCell>
                  <TableCell align="center">Meeting Organizer</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((inv) => {
                  const s = inv.schedule || {};
                  const owner = s.owner || {};
                  return (
                    <TableRow key={inv.id} hover>
                      <TableCell align="center">{s.id}</TableCell>
                      <TableCell align="center">{s.title}</TableCell>
                      <TableCell align="center">
                        {s.startDate ? formatDate(s.startDate, s.endDate) : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {`${owner.firstName || ""} ${owner.lastName || ""}`.trim()}
                        {owner.position ? ` - (${owner.position})` : ""}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          endIcon={<ChevronRightIcon />}
                          onClick={() => openSelectedMeetingModal(s.id)}
                          sx={{ fontSize: 13 }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* My Meetings */}
      <Typography sx={sectionTitleSx}>My Meetings</Typography>
      <TableContainer component={Paper}>
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={headRowSx}>
              <TableCell align="center">Meeting ID</TableCell>
              <TableCell align="center">Title</TableCell>
              <TableCell align="center">Date & Time</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Company</TableCell>
              <TableCell align="center">Meeting Organizer</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {meetings.length
              ? [...meetings].sort(byStartDateAsc).map((meeting) => (
                  <MeetingBox
                    key={meeting.id}
                    meeting={meeting}
                    openMeetingModal={openMeetingModalHandler}
                  />
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedMeeting && (
        <MeetingModal
          openMeetingModal={openMeetingModal}
          closeMeetingModalHandler={closeMeetingModalHandler}
          meeting={selectedMeeting}
          toggleRescheduleModal={toggleRescheduleModal}
          showShowAskForRescheduleModalHandler={askForRescheduleHandler}
          onRefresh={refreshAll}
        />
      )}

      {selectedMeeting && (
        <RescheduleModal
          openModal={openRescheduleModal}
          toggleModal={toggleRescheduleModal}
          meeting={selectedMeeting}
        />
      )}

      {selectedMeetingId && (
        <AskForRescheduleModal
          openResModal={showAskForRescheduleModal}
          closeResModalHandler={() => setShowAskForRescheduleModal(false)}
          meetingId={selectedMeetingId}
        />
      )}
    </>
  );
};

export default Meetings;
